// PlanerTab — verdrahtet PlanerWorkspace mit echtem Projektzustand.
// Holt Geodaten (Gebäude/Dachflächen), leitet Schritt-Abschluss + Freigabe-Blocker
// + KPIs ab und reicht die Schrittinhalte durch (bestehende Tabs via renderTab,
// Lücken 04/06 als eigene Komponenten). Keine hartkodierte Fachlogik — Werte aus API.
import { useEffect, useState, type ReactNode } from "react";
import { apiClient } from "@/lib/api-client.js";
import { num } from "@/lib/format.js";
import { PlanerWorkspace, type PlanerKpi, type PlanerStep } from "../PlanerWorkspace.js";
import { KonstruktionStep } from "./KonstruktionStep.js";
import { CadPlanStep } from "./CadPlanStep.js";

interface RoofSurface { roofType: string; polygon?: { lat: number; lng: number }[]; areaM2?: number }
interface Building { roofSurfaces?: RoofSurface[]; footprintPolygon?: { lat: number; lng: number }[] }

interface PlanerProject {
  id: string;
  name: string;
  address?: string;
  status?: string;
  offerNumber?: string;
  revisions?: { id: string }[];
}

const STATUS_LABELS: Record<string, string> = {
  draft: "In Planung",
  planning: "In Planung",
  ready_for_offer: "Angebotsbereit",
  offer_sent: "Angebot versandt",
};

export function PlanerTab({
  project,
  renderTab,
}: {
  project: PlanerProject;
  renderTab: (tabKey: string) => ReactNode;
}) {
  const [buildings, setBuildings] = useState<Building[]>([]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const res = await apiClient.get<{ buildings: Building[] }>(`/projects/${project.id}/geospatial`);
      if (alive && res.success) setBuildings(res.data?.buildings ?? []);
    })();
    return () => { alive = false; };
  }, [project.id]);

  const roofSurfaces = buildings.flatMap((b) => b.roofSurfaces ?? []);
  const drawnRoofs = roofSurfaces.filter((r) => r.roofType !== "obstacle" && (r.polygon?.length ?? 0) >= 3);
  const totalRoofArea = roofSurfaces
    .filter((r) => r.roofType !== "obstacle")
    .reduce((a, r) => a + (r.areaM2 ?? 0), 0);

  // Schritt-Abschluss aus echtem Datenbestand (pragmatisch, additiv erweiterbar).
  const completed: Record<string, boolean> = {
    projekt: !!project.name && !!project.address,
    dach: buildings.length > 0,
    module: false,
    konstr: false,
    anordnung: drawnRoofs.length > 0,
    cad: false,
    statik: false,
    edesign: false,
    elektrik: false,
    ausgabe: false,
    wirt: false,
  };

  // Freigabe-/Risiko-Kopplung: ohne bestätigte (gezeichnete) Dachfläche ist die
  // Angebotsfreigabe (Schritt 10/11) gesperrt.
  const blocker = drawnRoofs.length === 0
    ? { fromStepKey: "anordnung", reason: "Dachfläche bestätigen" }
    : null;

  const kpis: PlanerKpi[] = [
    { label: "Dachflächen", value: drawnRoofs.length > 0 ? String(drawnRoofs.length) : "–" },
    { label: "Dachfläche m²", value: totalRoofArea > 0 ? num(totalRoofArea, 0) : "–" },
    { label: "Gebäude", value: buildings.length > 0 ? String(buildings.length) : "–" },
    { label: "Status", value: STATUS_LABELS[project.status ?? ""] ?? "Entwurf", tone: "primary" },
  ];

  const renderStep = (step: PlanerStep): ReactNode => {
    if (step.tabKey === "konstruktion") return <KonstruktionStep />;
    if (step.tabKey === "cadplan") return <CadPlanStep projectId={project.id} />;
    return renderTab(step.tabKey);
  };

  return (
    <PlanerWorkspace
      projectName={project.name}
      statusLabel={STATUS_LABELS[project.status ?? ""] ?? "Entwurf"}
      {...(project.address ? { address: project.address } : {})}
      {...(project.offerNumber ? { angebotNr: project.offerNumber } : {})}
      {...(project.revisions && project.revisions.length > 0 ? { revisionLabel: `Rev. ${project.revisions.length} · aktiv` } : {})}
      kpis={kpis}
      completed={completed}
      blocker={blocker}
      renderStep={renderStep}
    />
  );
}
